/**
 * Admin delivery lists — the whole force, beat and vehicle wise.
 *
 * Two screens reached from the Daily field performance tiles on the admin
 * dashboard:
 *
 *   PendingDeliveryList  — every order still to be delivered, across all
 *                          drivers, vans and beats (GET /api/orders/pending-delivery).
 *   DeliveredTodayList   — every delivery made today, same breadth
 *                          (GET /api/orders/delivered-today).
 *
 * Both group by beat so the office reads the round the way it is run, and each
 * row names the driver and van carrying it. Polled while open so the figures
 * track the field without a refresh — the dashboard tiles poll the same way.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page } from '../../components/Shell';
import { CustomerName } from '../../components/CustomerName';
import {
  Card, Chip, Empty, ErrorState, Money, Skeleton,
} from '../../components/ui';
import { useQuery, useRefreshOnFocus, usePoll } from '../../lib/useApi';
import { dateShort } from '../../lib/format';

interface PendingRow {
  id: string;
  order_number: string;
  net_amount_paise: number;
  shop_name: string;
  address_line1: string | null;
  legal_name: string | null;
  trade_name: string | null;
  customer_classification: string | null;
  invoice_id: string | null;
  invoice_number: string | null;
  beat_name: string | null;
  beat_code: string | null;
  driver_code: string | null;
  driver_name: string | null;
  registration_number: string | null;
}

interface DeliveredRow {
  id: string;
  order_number: string;
  net_amount_paise: number;
  delivered_at: string;
  outcome: string;
  shop_name: string;
  address_line1: string | null;
  legal_name: string | null;
  trade_name: string | null;
  customer_classification: string | null;
  invoice_number: string | null;
  beat_name: string | null;
  beat_code: string | null;
  driver_code: string | null;
  driver_name: string | null;
  registration_number: string | null;
  delivered_by_name: string | null;
}

interface CrewRow {
  beat_name: string | null;
  driver_name: string | null;
  registration_number: string | null;
}

/**
 * Group rows under their beat, in a stable order, so the list reads by round.
 * Each group also carries the distinct crews (driver · van) working that beat,
 * for the header line — one line per round, however many drivers share it.
 */
function groupByBeat<T extends CrewRow>(
  rows: T[],
): { beat: string; crews: string[]; items: T[] }[] {
  const map = new Map<string, { beat: string; crewSet: Set<string>; items: T[] }>();
  for (const r of rows) {
    const key = r.beat_name ?? 'No beat';
    const g = map.get(key) ?? { beat: key, crewSet: new Set<string>(), items: [] };
    g.items.push(r);
    const c = crew(r.driver_name, r.registration_number);
    if (c) g.crewSet.add(c);
    map.set(key, g);
  }
  return [...map.values()]
    .map((g) => ({ beat: g.beat, crews: [...g.crewSet], items: g.items }))
    .sort((a, b) => a.beat.localeCompare(b.beat));
}

/** "Ajay Kumar · RJ14GV3141" — driver and van, whichever is known; '' if neither. */
function crew(driverName: string | null, reg: string | null): string {
  return [driverName, reg].filter(Boolean).join(' · ');
}

export function PendingDeliveryList() {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useQuery<{ items: PendingRow[] }>(
    '/api/orders/pending-delivery',
  );
  useRefreshOnFocus(refetch);
  usePoll(refetch, 15000);

  const groups = useMemo(() => groupByBeat(data?.items ?? []), [data]);
  const total = data?.items.length ?? 0;

  return (
    <Page title="Orders Pending for Delivery" subtitle={total ? `${total} pending` : undefined} back>
      <div className="stack">
        {loading && !data && <Skeleton rows={4} height={72} />}
        {error && !data && <ErrorState message={error.message} onRetry={refetch} />}
        {data && total === 0 && (
          <Empty title="Nothing pending" hint="Every routed order has been delivered." />
        )}
        {groups.map((g) => (
          <Card key={g.beat} flush>
            <div
              className="card__header"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
            >
              {/* Beat NAME only (no code), with the driver & van working it on
                  the same line. Several crews on one beat read comma-separated. */}
              <h3 style={{ margin: 0, minWidth: 0 }}>
                {g.beat}
                {g.crews.length > 0 && (
                  <span className="t-sm t-muted" style={{ fontWeight: 500 }}>
                    {' — '}{g.crews.join(', ')}
                  </span>
                )}
              </h3>
              <Chip tone="warn">{g.items.length} pending</Chip>
            </div>
            <div className="list">
              {g.items.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className="list__item"
                  onClick={() => navigate(`/orders/${o.id}`)}
                >
                  <div className="grow" style={{ minWidth: 0 }}>
                    <CustomerName name={o.trade_name || o.legal_name || o.shop_name} classification={o.customer_classification} className="t-semi" />
                    <div className="t-xs t-muted t-clip">
                      {o.shop_name}{o.address_line1 ? ` · ${o.address_line1}` : ''}
                    </div>
                    <div className="t-xs t-muted">{o.invoice_number ?? o.order_number}</div>
                  </div>
                  <Money paise={o.net_amount_paise} className="t-sm" />
                </button>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </Page>
  );
}

export function DeliveredTodayList() {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useQuery<{ items: DeliveredRow[] }>(
    '/api/orders/delivered-today',
  );
  useRefreshOnFocus(refetch);
  usePoll(refetch, 15000);

  const groups = useMemo(() => groupByBeat(data?.items ?? []), [data]);
  const total = data?.items.length ?? 0;

  return (
    <Page title="Orders Delivered Today" subtitle={total ? `${total} delivered` : undefined} back>
      <div className="stack">
        {loading && !data && <Skeleton rows={4} height={72} />}
        {error && !data && <ErrorState message={error.message} onRetry={refetch} />}
        {data && total === 0 && (
          <Empty title="Nothing delivered yet" hint="Completed deliveries appear here as drivers mark them." />
        )}
        {groups.map((g) => (
          <Card key={g.beat} flush>
            <div
              className="card__header"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
            >
              <h3 style={{ margin: 0, minWidth: 0 }}>
                {g.beat}
                {g.crews.length > 0 && (
                  <span className="t-sm t-muted" style={{ fontWeight: 500 }}>
                    {' — '}{g.crews.join(', ')}
                  </span>
                )}
              </h3>
              <Chip tone="ok">{g.items.length} delivered</Chip>
            </div>
            <div className="list">
              {g.items.map((o) => (
                <button
                  key={`${o.id}-${o.delivered_at}`}
                  type="button"
                  className="list__item"
                  onClick={() => navigate(`/orders/${o.id}`)}
                >
                  <div className="grow" style={{ minWidth: 0 }}>
                    <CustomerName name={o.trade_name || o.legal_name || o.shop_name} classification={o.customer_classification} className="t-semi" />
                    <div className="t-xs t-muted t-clip">
                      {o.shop_name}{o.address_line1 ? ` · ${o.address_line1}` : ''}
                    </div>
                    <div className="t-xs t-muted">{o.invoice_number ?? o.order_number}</div>
                  </div>
                  <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
                    <Money paise={o.net_amount_paise} className="t-sm" />
                    <div className="t-xs t-muted">{dateShort(o.delivered_at)}</div>
                  </div>
                </button>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </Page>
  );
}
